import * as React from 'react';

/**
 * PromotionModal 이 모바일 레이아웃(하단 카드)과 PC 레이아웃(중앙 다이얼로그)을
 * 가르는 SSR-safe 뷰포트 훅. ZUS 에는 아직 공개 뷰포트 훅이 없어(패키지 내부
 * `useViewport` 는 export 미개방) 포터블하게 로컬 구현한다.
 *
 * 브레이크포인트는 quiz-app `useViewport` 의 `isMobile = isMobileSm || isMobileLg`
 * (max-width 560px)와 동일하게 맞춰 이관 전 동작을 보존한다.
 *
 * 서버(window 없음)에서는 항상 false 를 돌려주고, 마운트 후 matchMedia 결과로
 * 동기화한다(hydration mismatch 없이 클라에서만 모바일 분기 활성).
 */
const MOBILE_MAX_WIDTH_PX = 560;

export function useIsMobile(): boolean {
  const query = `(max-width: ${MOBILE_MAX_WIDTH_PX}px)`;

  const subscribe = React.useCallback(
    (onChange: () => void) => {
      if (typeof window === 'undefined' || !window.matchMedia) {
        return () => undefined;
      }
      const mql = window.matchMedia(query);
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    },
    [query],
  );

  const getSnapshot = React.useCallback(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  }, [query]);

  // 서버 스냅샷은 항상 false(모바일 분기 비활성 → PC 마크업으로 SSR).
  const getServerSnapshot = React.useCallback(() => false, []);

  return React.useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
